import React, { useCallback, useEffect, useState } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import {
    applyCheckinTemplate,
    deleteCheckinTemplate,
    getCheckinTemplates,
    updateCheckinTemplate,
    Checkin,
    CheckinTemplate,
    CheckinTemplateUpdate,
} from '../../api/client'
import ConfirmationModal from '../ConfirmationModal'
import CheckinTemplateSettingsModal from './CheckinTemplateSettingsModal'
import { useLongPress } from '../../hooks/useLongPress'
import { fadeRightStyle, useHorizontalOverflow } from '../../hooks/useHorizontalOverflow'

/**
 * The row of one-tap repeat checkins.
 *
 * **The user calls these shortcuts; the code calls them templates.** Every
 * string a reader sees — the ⋯ menu item, the save toast, the delete
 * confirmation, the nudge — says "shortcut". Identifiers, routes, tables and
 * filenames stay `checkin_template*`, deliberately and permanently: renaming
 * them buys nothing a reader can see. So a "template" in this file is a thing
 * in the database, and if you are writing a sentence the user will read, the
 * word is shortcut.
 *
 * Tap a pill and the server materializes a checkin from the template's
 * snapshot — no parse, no LLM — which the page drops in at the top of the
 * timeline, read-only. Hold a pill and its settings open: what time its
 * check-ins get, and the way to delete it.
 *
 * There is no section label above the row on purpose: with pills the row
 * explains itself, and with no templates it renders nothing at all, so a
 * heading would be a permanent frame around an occasional thing.
 *
 * The component owns the template list and every call that changes it. The
 * page's only inbound channel is `insertedTemplate` — the row it got back from
 * "save as template" — which is merged in without a refetch.
 *
 * Two pages render it: the Checkins page, where `onApplied` lands the new
 * checkin in the list underneath; and the Dashboard, where `onApplied`
 * navigates to Checkins carrying the new checkin's id so the row gets the same
 * entrance there. The apply itself is the component's either way — only where
 * the result goes differs, which is why `mode` changes the box and nothing else.
 */

/** The row shows at most this many; the rest exist but stay off the pill row. */
export const MAX_TEMPLATE_PILLS = 6

const LABEL_MAX_LENGTH = 24
const WHITESPACE_RE = /\s+/g

/**
 * Mirror of `derive_template_label` in
 * `backend/server/features/checkin_templates.py`: whitespace collapsed, case
 * and punctuation kept (this is display text, not an identity), truncated to
 * 24 characters with an ellipsis.
 *
 * The server fills `label` on every template it returns, so this is a fallback
 * rather than the usual path — but it has to agree with the server exactly,
 * because the moment the two disagree a pill silently renames itself on the
 * next reload.
 */
export const deriveTemplateLabel = (body: string | null | undefined): string => {
    const text = (body ?? '').replace(WHITESPACE_RE, ' ').trim()
    if (text.length <= LABEL_MAX_LENGTH) return text
    return text.slice(0, LABEL_MAX_LENGTH - 1).replace(/\s+$/, '') + '…'
}

/**
 * Most-useful-first, capped at `limit`.
 *
 * Same ordering the list endpoint already applies — use count, then recency,
 * then id as a stable tiebreak — repeated here because the row re-sorts
 * locally after an apply bumps a template's count, and a second round-trip
 * just to learn the new order would undo the point of the one-tap path.
 * Never mutates its argument.
 */
export const orderTemplatesForPills = (
    templates: CheckinTemplate[],
    limit: number = MAX_TEMPLATE_PILLS,
): CheckinTemplate[] => {
    const usedAt = (template: CheckinTemplate): number =>
        template.last_used_at ? new Date(template.last_used_at).getTime() : 0
    return [...templates]
        .sort((a, b) => {
            if (a.use_count !== b.use_count) return b.use_count - a.use_count
            const recency = usedAt(b) - usedAt(a)
            if (recency !== 0) return recency
            return b.id - a.id
        })
        .slice(0, limit)
}

/**
 * Where the row is rendered. The pills, their motion and everything they do are
 * identical in both; only the row's own box changes, because the two hosts
 * indent their content differently.
 *
 * - `checkins` — inside the filter column, which supplies the side inset and
 *   carries no flex gap, so the row owns its bottom spacing.
 * - `dashboard` — directly inside the page container, between the activity
 *   chart and the summaries, so the row has to reproduce the inset the summary
 *   cards get from their own wrapper (16px on mobile, 32px from `sm` up) or the
 *   pills would sit flush against the viewport edge while the cards below
 *   don't.
 */
type CheckinTemplatePillsMode = 'checkins' | 'dashboard'

/* `pt-1 -mt-1` buys the scroll container four pixels of slack at the top, then
   takes them back out of the layout: the chips' scale overshoot and the
   one-pixel hover lift would otherwise be clipped by `overflow-x-auto`, which
   clips vertically too.
   On the Dashboard that top padding does double duty below `sm`, where the
   chart above has no bottom margin of its own: `pt-5 -mt-1` is the same four
   pixels of slack plus the 16px everything on that page sits apart by. There
   the row's own `pb` stays 0 — the summaries wrapper's `p-4` already supplies
   16px below — while from `sm` up, where the chart's `mb-8` opens 32px above,
   `pb-4` pairs with that `p-4` to put 32px below.

   The scrollbar itself is hidden, the same way `CalendarSummaryView` and
   `StreakStripView` hide theirs: a bar under a single line of chips is most of
   that line's height and reads as chrome the row can't afford. Those two pair
   the hidden bar with paging arrows, but this row carries at most six short
   chips and usually fewer than fit, so there is normally nothing to page —
   swipe on touch and shift-wheel on a trackpad cover the overflow case. What
   the row does need when it overflows is a sign that it does: on a phone the
   last visible chip is cut mid-word, which reads as clipping rather than as
   more to the right. So the right edge fades while there is more to swipe to
   (`fadeRightStyle`, the same hint the LLM-usage tables use). */
const SCROLL_CLASSES =
    'overflow-x-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden'

const ROW_CLASSES: Record<CheckinTemplatePillsMode, string> = {
    checkins: `flex items-center gap-2 ${SCROLL_CLASSES} pt-1 -mt-1 pb-4`,
    dashboard: `flex items-center gap-2 ${SCROLL_CLASSES} -mt-1 pt-5 sm:pt-1 sm:pb-4 px-4 sm:px-8`,
}

interface CheckinTemplatePillsProps {
    /** A checkin the server just made from a template, ready to prepend. */
    onApplied: (checkin: Checkin) => void
    /** A template the page just saved, merged into the row without a refetch. */
    insertedTemplate?: CheckinTemplate | null
    /** Raised by the page as an error notice. */
    onError?: (message: string) => void
    /** Which host is rendering the row; defaults to the Checkins page. */
    mode?: CheckinTemplatePillsMode
}

const CheckinTemplatePills: React.FC<CheckinTemplatePillsProps> = ({
    onApplied,
    insertedTemplate,
    onError,
    mode = 'checkins',
}) => {
    const [templates, setTemplates] = useState<CheckinTemplate[]>([])
    const [applyingId, setApplyingId] = useState<number | null>(null)
    const [settingsFor, setSettingsFor] = useState<CheckinTemplate | null>(null)
    const [pendingDelete, setPendingDelete] = useState<CheckinTemplate | null>(null)
    const [isDeleting, setIsDeleting] = useState(false)
    const scroll = useHorizontalOverflow<HTMLDivElement>()

    useEffect(() => {
        let cancelled = false
        getCheckinTemplates()
            .then(loaded => {
                if (!cancelled) setTemplates(loaded)
            })
            .catch(err => {
                // A missing pill row is not worth an error notice over the
                // timeline the user actually came for.
                console.error('Error loading checkin templates:', err)
            })
        return () => {
            cancelled = true
        }
    }, [])

    // Merge by id, so an idempotent re-save of an existing template re-sorts
    // the row rather than doubling the pill.
    useEffect(() => {
        if (!insertedTemplate) return
        setTemplates(prev => [
            insertedTemplate,
            ...prev.filter(template => template.id !== insertedTemplate.id),
        ])
    }, [insertedTemplate])

    const handleApply = useCallback(async (template: CheckinTemplate) => {
        if (applyingId !== null) return
        setApplyingId(template.id)
        try {
            const checkin = await applyCheckinTemplate(template.id)
            onApplied(checkin)
            // Bump locally to match what the server just wrote, so the row
            // re-sorts under the `layout` animation instead of on next load.
            setTemplates(prev => prev.map(existing => existing.id === template.id
                ? {
                    ...existing,
                    use_count: existing.use_count + 1,
                    last_used_at: new Date().toISOString(),
                }
                : existing))
        } catch (err) {
            console.error('Error applying checkin template:', err)
            onError?.('Failed to add a checkin from that shortcut')
        } finally {
            setApplyingId(null)
        }
    }, [applyingId, onApplied, onError])

    // The server's row wins, rather than a local patch of the one field: the
    // PATCH returns the refreshed template and the list holds nothing the
    // response doesn't. A failure leaves the old row in place, so the radio
    // snaps back to what is actually stored instead of showing a mode that
    // never persisted. Rethrown so the modal can put its time picker back too.
    const handleUpdate = useCallback(async (
        template: CheckinTemplate,
        update: CheckinTemplateUpdate,
    ) => {
        try {
            const updated = await updateCheckinTemplate(template.id, update)
            setTemplates(prev => prev.map(existing =>
                existing.id === updated.id ? updated : existing))
            setSettingsFor(current =>
                current && current.id === updated.id ? updated : current)
        } catch (err) {
            console.error('Error updating checkin template time:', err)
            onError?.('Failed to change when that shortcut lands')
            throw err
        }
    }, [onError])

    const handleConfirmDelete = useCallback(async () => {
        if (!pendingDelete) return
        setIsDeleting(true)
        try {
            await deleteCheckinTemplate(pendingDelete.id)
            setTemplates(prev => prev.filter(template => template.id !== pendingDelete.id))
            setPendingDelete(null)
            // The settings modal is the only way here, and it is describing a
            // template that no longer exists.
            setSettingsFor(null)
        } catch (err) {
            console.error('Error deleting checkin template:', err)
            onError?.('Failed to delete that shortcut')
        } finally {
            setIsDeleting(false)
        }
    }, [pendingDelete, onError])

    const visible = orderTemplatesForPills(templates)

    return (
        <>
            {visible.length > 0 && (
                <div ref={scroll.ref} className={ROW_CLASSES[mode]} style={fadeRightStyle(scroll)}>
                    <AnimatePresence initial={false}>
                        {visible.map(template => (
                            <TemplatePill
                                key={template.id}
                                template={template}
                                disabled={applyingId !== null}
                                onTap={() => handleApply(template)}
                                onLongPress={() => setSettingsFor(template)}
                            />
                        ))}
                    </AnimatePresence>
                </div>
            )}

            <CheckinTemplateSettingsModal
                template={settingsFor}
                onClose={() => setSettingsFor(null)}
                onUpdate={handleUpdate}
                onDelete={setPendingDelete}
            />

            <ConfirmationModal
                isOpen={pendingDelete !== null}
                onClose={() => setPendingDelete(null)}
                onConfirm={handleConfirmDelete}
                title="Delete shortcut"
                message={pendingDelete
                    ? `Delete the "${pendingDelete.label}" shortcut? Checkins you already made from it stay.`
                    : ''}
                confirmText="Delete"
                cancelText="Cancel"
                confirmButtonClass="bg-danger hover:bg-danger-dark text-white"
                isLoading={isDeleting}
            />
        </>
    )
}

interface TemplatePillProps {
    template: CheckinTemplate
    disabled: boolean
    onTap: () => void
    onLongPress: () => void
}

const TemplatePill: React.FC<TemplatePillProps> = ({ template, disabled, onTap, onLongPress }) => {
    const longPress = useLongPress({ onTap, onLongPress })

    return (
        <motion.button
            type="button"
            layout
            style={{ transformOrigin: 'left center' }}
            initial={{ opacity: 0, scale: 0.6 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.6 }}
            transition={{ type: 'spring', stiffness: 500, damping: 30 }}
            whileHover={{ y: -1 }}
            whileTap={{ scale: 0.9 }}
            disabled={disabled}
            {...longPress}
            className="shrink-0 inline-flex items-center rounded-full text-sm font-medium bg-secondary text-gray-700 hover:bg-secondary-dark px-3 py-1 transition-colors cursor-pointer whitespace-nowrap select-none disabled:opacity-60 disabled:cursor-default focus:outline-none focus:ring-2 focus:ring-action"
            title={`Check in again: ${template.body}`}
        >
            {template.label || deriveTemplateLabel(template.body)}
        </motion.button>
    )
}

export default CheckinTemplatePills
